import type { PlaceDetails, ParsedAddress, AutocompleteOption, SelectedOption } from "./google-places";
import { ADDRESS_COMPONENT_TYPE_MAPPING, PLACES_API_CONFIG } from "./google-places-constants";
import type { GooglePlacePrediction } from "./use-places-api";

export const createAddressParser = () => {
  return (addressComponents: PlaceDetails["addressComponents"]): ParsedAddress => {
    if (!addressComponents?.length) {
      return { formattedAddress: "" };
    }

    const parsed: ParsedAddress = { formattedAddress: "" };
    const fieldPrecedence: Record<string, number> = {};

    addressComponents.forEach((component) => {
      const matchingType = component.types.find((type) => type in ADDRESS_COMPONENT_TYPE_MAPPING);

      if (matchingType) {
        const mapping = ADDRESS_COMPONENT_TYPE_MAPPING[matchingType as keyof typeof ADDRESS_COMPONENT_TYPE_MAPPING];
        const textValue = "useShortText" in mapping && mapping.useShortText ? component.shortText : component.longText;
        const currentPrecedence = fieldPrecedence[mapping.field] ?? Infinity;

        if (mapping.precedence < currentPrecedence) {
          parsed[mapping.field] = textValue;
          fieldPrecedence[mapping.field] = mapping.precedence;
        }
      }
    });

    const addressParts = [
      parsed.streetNumber && parsed.streetName ? `${parsed.streetNumber} ${parsed.streetName}` : parsed.streetName,
      parsed.city,
      parsed.state,
      parsed.postalCode,
      parsed.country,
    ].filter(Boolean);

    parsed.formattedAddress = addressParts.join(", ");
    return parsed;
  };
};

const createStreetAddressFallback = (placeDetails: PlaceDetails, city?: string, state?: string): string | undefined => {
  try {
    const addressParts = placeDetails.formattedAddress.split(", ");
    const firstPart = addressParts[0];

    if (!firstPart || firstPart.length < 2) {
      return undefined;
    }

    const isLikelyCityState =
      /^[A-Z]{2}$/.test(firstPart) ||
      addressParts.length <= 2 ||
      firstPart === city ||
      firstPart === state ||
      /^\d+$/.test(firstPart);

    if (isLikelyCityState) {
      return undefined;
    }

    if (firstPart.length > PLACES_API_CONFIG.addressSplitThreshold) {
      const words = firstPart.split(" ");
      if (words.length > 3) {
        const midPoint = Math.ceil(words.length / 2);
        return words.slice(0, midPoint).join(" ");
      }
    }

    return firstPart.length <= PLACES_API_CONFIG.maxAddressLength ? firstPart : undefined;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn("Failed to create street address fallback:", error);
    return undefined;
  }
};

export const createAddressFieldSetters = <T>(
  form: { setValue: (name: keyof T, value: string) => void },
  fieldMapping: {
    streetAddress?: keyof T;
    city?: keyof T;
    state?: keyof T;
    postalCode?: keyof T;
    country?: keyof T;
    formattedAddress?: keyof T;
  }
) => {
  const clearAddressFields = (): void => {
    Object.values(fieldMapping).forEach((formField) => {
      if (formField) {
        form.setValue(formField, "");
      }
    });
  };

  const setAddressFields = (address: ParsedAddress, placeDetails: PlaceDetails): void => {
    const { streetNumber, streetName, city, state, postalCode, country } = address;

    let streetAddress = [streetNumber, streetName].filter(Boolean).join(" ") || undefined;
    if (!streetAddress) {
      streetAddress = createStreetAddressFallback(placeDetails, city, state);
    }

    const fieldValues: Record<keyof typeof fieldMapping, string | undefined> = {
      streetAddress,
      city,
      state,
      postalCode,
      country,
      formattedAddress: placeDetails.formattedAddress,
    };

    clearAddressFields();

    Object.entries(fieldMapping).forEach(([field, formField]) => {
      const value = fieldValues[field as keyof typeof fieldValues];
      if (formField && value?.trim()) {
        form.setValue(formField, value.trim());
      }
    });
  };

  return Object.assign(setAddressFields, { clear: clearAddressFields });
};

export const processAutocompleteOptions = (
  predictions: Array<{ placePrediction: GooglePlacePrediction }>,
  selectedOption: SelectedOption | null
): AutocompleteOption[] => {
  const createOption = (prediction: GooglePlacePrediction): AutocompleteOption => ({
    id: prediction.placeId,
    text: prediction.text.text,
    label: prediction.text.text,
    value: prediction.placeId,
    subText: prediction.structuredFormat?.secondaryText?.text || "",
    prediction: {
      place_id: prediction.placeId,
      description: { text: prediction.text.text },
      structured_formatting: {
        main_text: { text: prediction.structuredFormat?.mainText?.text || prediction.text.text },
        secondary_text: { text: prediction.structuredFormat?.secondaryText?.text || "" },
      },
    },
  });

  const options = predictions.map(({ placePrediction }) => createOption(placePrediction));

  if (selectedOption) {
    const existingIndex = options.findIndex((opt) => opt.id === selectedOption.id);

    if (existingIndex >= 0) {
      const option = options[existingIndex];
      option.text = selectedOption.text;
      option.label = selectedOption.text;
      option.prediction.description.text = selectedOption.text;
      option.prediction.structured_formatting.main_text.text = selectedOption.text;
    } else {
      options.unshift({
        id: selectedOption.id,
        text: selectedOption.text,
        label: selectedOption.text,
        value: selectedOption.id,
        subText: "",
        prediction: {
          place_id: selectedOption.id,
          description: { text: selectedOption.text },
          structured_formatting: {
            main_text: { text: selectedOption.text },
            secondary_text: { text: "" },
          },
        },
      });
    }
  }

  return options;
};
